import React, { useState, useMemo } from 'react';
import { Plus, Trash2, Tag, X, Save, Ban, CheckCircle2 } from 'lucide-react';
import { useServices, useDeleteConfirmation } from '@/app/providers';
import { Category } from '@/shared/types';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { useCrudNotifications, usePaginatedList } from '@/shared/hooks';
import { DataTable, type ColumnDef } from '@/core/ui/components';
import { useAbility } from '@/modules/identity/application/ability';

const COLOR_OPTIONS = ['#7F77DD', '#4FA3D1', '#E5B800', '#639922', '#E24B4A', '#9C6ADE', '#EF9F27', '#888780'];

export const AdminCategoryList = () => {
  const { categoryRepo } = useServices();
  const { confirmDelete } = useDeleteConfirmation();
  const { withCreateNotification, withUpdateNotification, withDeleteNotification } = useCrudNotifications({
    entityName: 'Categoría',
  });

  const {
    data: categories,
    meta,
    loading,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    refresh,
  } = usePaginatedList({
    fetchFn: (params) => categoryRepo.getCategories(params),
    errorTitle: 'Error al cargar categorías',
  });

  // El admin de sección solo lee el catálogo: cambiarlo afecta a todo el residencial.
  const ability = useAbility();
  const canManage = ability.can('manage', 'all') || ability.can('manage', 'Category');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [form, setForm] = useState<{ name: string; color: string }>({
    name: '',
    color: COLOR_OPTIONS[0],
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', color: COLOR_OPTIONS[0] });
    setIsModalOpen(true);
  };

  const openEdit = (category: Category) => {
    setEditing(category);
    setForm({ name: category.name, color: category.color ?? COLOR_OPTIONS[0] });
    setIsModalOpen(true);
  };

  const handleDelete = async (category: Category) => {
    await confirmDelete({
      itemName: category.name,
      itemType: 'Categoría',
      onConfirm: async () => {
        await withDeleteNotification(() => categoryRepo.deleteCategory(category.id));
        refresh();
      },
    });
  };

  const toggleActive = async (category: Category) => {
    await withUpdateNotification(() =>
      categoryRepo.updateCategory(category.id, { isActive: !category.isActive })
    );
    refresh();
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    try {
      if (editing) {
        await withUpdateNotification(() =>
          categoryRepo.updateCategory(editing.id, { name: form.name, color: form.color })
        );
      } else {
        await withCreateNotification(() =>
          categoryRepo.createCategory({ name: form.name, color: form.color, kind: 'EXPENSE' })
        );
      }
      setIsModalOpen(false);
      refresh();
    } catch {
      // Error ya mostrado por el hook
    }
  };

  const columns: ColumnDef<Category>[] = useMemo(() => [
    {
      id: 'name',
      header: 'Nombre',
      cell: (category) => (
        <div className="flex items-center gap-2">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: category.color ?? '#888780' }}
          />
          <p className="font-medium text-slate-800">{category.name}</p>
          {category.isSystem && (
            <span className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">
              Sistema
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Estado',
      cell: (category) => (
        <button
          onClick={() => toggleActive(category)}
          disabled={!canManage}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium ${
            category.isActive ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'
          }`}
        >
          {category.isActive ? <CheckCircle2 size={12} /> : <Ban size={12} />}
          {category.isActive ? 'Activa' : 'Inactiva'}
        </button>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canManage]);

  return (
    <>
      <DataTable
        data={categories}
        columns={columns}
        keyExtractor={(category) => category.id}
        meta={meta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        title="Categorías de egresos"
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar categoría..."
        loading={loading}
        emptyMessage="No hay categorías registradas."
        actions={
          canManage && (
            <Button onClick={openCreate} icon={<Plus size={16} />}>
              Nueva categoría
            </Button>
          )
        }
        rowActions={(category) => canManage && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => openEdit(category)}
              className="p-1.5 text-slate-400 hover:text-primary-600 rounded transition-colors"
            >
              <Tag size={16} />
            </button>
            {!category.isSystem && (
              <button
                onClick={() => handleDelete(category)}
                className="p-1.5 text-slate-400 hover:text-red-600 rounded transition-colors"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        )}
      />

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">
                {editing ? 'Editar categoría' : 'Nueva categoría'}
              </h3>
              <button onClick={() => setIsModalOpen(false)}>
                <X size={20} className="text-slate-400" />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4">
              <Input
                label="Nombre"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Color</label>
                <div className="flex gap-2 flex-wrap">
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setForm({ ...form, color })}
                      className={`w-7 h-7 rounded-full border-2 ${
                        form.color === color ? 'border-slate-800' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
                  Cerrar
                </Button>
                <Button type="submit" variant="primary" className="flex-1" icon={<Save size={16} />}>
                  Guardar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
